// src/components/FamilyOfficeReportButton.jsx
// Bouton « Family Office Report » (§9.3/§17 Phase 2) — reporting consolidé multi-familles.
// Réutilise le moteur commun (computePortfolioReadiness) sur artworks_secure (RLS).
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { computePortfolioReadiness, readinessPriority, READINESS_SELECT_SECURE } from "../utils/readiness";
import { generateFamilyOfficeReport } from "../utils/familyOfficeReportPdf";

const COPY = {
  fr: { fail: "Impossible de générer le Family Office Report.", title: "Générer un reporting patrimonial consolidé (par famille + points d'attention)", busy: "Génération…", label: "Family Office Report (PDF)" },
  en: { fail: "Unable to generate the Family Office Report.", title: "Generate a consolidated wealth report (per family + points of attention)", busy: "Generating…", label: "Family Office Report (PDF)" },
  de: { fail: "Der Family Office Report konnte nicht erstellt werden.", title: "Konsolidierten Vermögensbericht erstellen (je Familie + Aufmerksamkeitspunkte)", busy: "Wird erstellt…", label: "Family Office Report (PDF)" },
};

const dimMissing = (cr, key) => (cr?.dimensions || []).find((d) => d.key === key)?.missing || 0;

export default function FamilyOfficeReportButton({ clients = [], foName = "", className = "" }) {
  const { i18n } = useTranslation();
  const c = COPY[String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2)] || COPY.fr;
  const [busy, setBusy] = useState(false);
  const clientIds = [...new Set((clients || []).map((c) => c.id).filter(Boolean))];

  const run = async () => {
    if (busy || clientIds.length === 0) return;
    setBusy(true);
    try {
      const { data, error } = await supabase
        .from("artworks_secure")
        .select(READINESS_SELECT_SECURE)
        .in("client_id", clientIds);
      if (error) throw error;

      const portfolio = computePortfolioReadiness(data || []);

      const families = (clients || []).map((c) => {
        const cr = portfolio.perClient[c.id];
        const readiness = cr ? cr.overall : 0;
        return { name: c.name || "—", value: c.totalValue || 0, readiness, priority: readinessPriority(readiness) };
      });

      const attention = { valuation: 0, insurance: 0, documentation: 0, provenance: 0, conservation: 0, exceptional: 0 };
      for (const cr of Object.values(portfolio.perClient)) {
        attention.valuation += dimMissing(cr, "valuation");
        attention.insurance += dimMissing(cr, "insurance");
        attention.documentation += dimMissing(cr, "documentation");
        attention.provenance += dimMissing(cr, "provenance");
        attention.conservation += dimMissing(cr, "conservation");
        attention.exceptional += cr?.exceptionalUnprepared || 0;
      }

      generateFamilyOfficeReport({ foName, overall: portfolio.overall, families, attention });
    } catch (e) {
      console.error("[FamilyOfficeReportButton]", e);
      window.alert(c.fail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || clientIds.length === 0}
      title={c.title}
      className={className || "inline-flex items-center gap-2 rounded-lg bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white hover:bg-black disabled:opacity-60"}
    >
      {busy ? c.busy : c.label}
    </button>
  );
}
